// src/admin/hooks/useSystemConfig.ts
// Reads public.system_config and stitches the updater admin via a second
// query on admin_users.
//
// Deviations from the draft plan (plan at 2026-04-18-admin-console.md:8755):
//   - The DB column is `key`, not `config_key`. This hook exposes it as
//     `config_key` in the public type so screen/modal reads stay aligned
//     with the plan's nomenclature; we alias on the select (`config_key:key`).
//   - The values column is NOT a single `value: jsonb`. Migration
//     20260418000008_create_system_config.sql splits the payload across
//     three nullable columns (`value_text`, `value_int`, `value_json`) with
//     an "exactly one non-null" CHECK constraint. The hook unifies this
//     into a single `{ kind, value }` discriminated pair so consumers don't
//     have to know about the storage shape. UpdateConfigModal uses the
//     `kind` to route writes back to the correct column.
//   - `admin_users.Relationships: []` in database.types → a PostgREST
//     `admin_users!fkey(...)` join fails to type-check. Use the same
//     batched `.in('id', ids)` follow-up pattern used by useAuditLog.
//   - No joined `updated_by` record in the result; instead expose
//     `updated_by` as `{ display_name, email } | null` for easy display.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import type { Json } from '@/lib/database.types';

export type ConfigValueKind = 'text' | 'int' | 'json';

export interface ConfigRow {
  id: string;
  config_key: string;
  kind: ConfigValueKind;
  value: string | number | Json;
  description: string;
  updated_at: string;
  updated_by_admin_id: string | null;
  updated_by: { display_name: string | null; email: string } | null;
}

interface RawConfigRow {
  id: string;
  config_key: string; // aliased from `key`
  value_text: string | null;
  value_int: number | null;
  value_json: Json | null;
  description: string;
  updated_at: string;
  updated_by_admin_id: string | null;
}

function resolveKindAndValue(row: RawConfigRow): { kind: ConfigValueKind; value: string | number | Json } {
  if (row.value_text !== null) return { kind: 'text', value: row.value_text };
  if (row.value_int !== null) return { kind: 'int', value: row.value_int };
  // The CHECK constraint guarantees exactly one column is set, so if neither
  // text nor int is populated we can trust value_json is present. A defensive
  // fallback to null keeps consumers safe if the constraint is ever relaxed.
  return { kind: 'json', value: row.value_json ?? null };
}

export function useSystemConfig() {
  return useQuery<ConfigRow[]>({
    queryKey: ['system-config'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_config')
        .select(
          'id, config_key:key, value_text, value_int, value_json, description, updated_at, updated_by_admin_id',
        )
        .order('key');
      if (error) throw error;
      const raws = (data ?? []) as unknown as RawConfigRow[];
      if (raws.length === 0) return [];

      const adminIds = Array.from(
        new Set(raws.map((r) => r.updated_by_admin_id).filter((x): x is string => !!x)),
      );
      let adminMap = new Map<string, { display_name: string | null; email: string }>();
      if (adminIds.length > 0) {
        const { data: admins, error: adminErr } = await supabase
          .from('admin_users')
          .select('id, display_name, email')
          .in('id', adminIds);
        if (adminErr) throw adminErr;
        adminMap = new Map(
          (admins ?? []).map((a) => [a.id, { display_name: a.display_name, email: a.email }]),
        );
      }

      return raws.map((r): ConfigRow => {
        const { kind, value } = resolveKindAndValue(r);
        return {
          id: r.id,
          config_key: r.config_key,
          kind,
          value,
          description: r.description,
          updated_at: r.updated_at,
          updated_by_admin_id: r.updated_by_admin_id,
          updated_by: r.updated_by_admin_id ? adminMap.get(r.updated_by_admin_id) ?? null : null,
        };
      });
    },
  });
}
